import React, { useEffect, useRef, useState } from "react";
import "./intro.css";
import GeistComponent from "./geist";
import "./terminal.css";
import { DvdLogoAnimation } from "../util/dvdLogoAnimation";
import { StarWarsAnimation } from "../util/starWarsAnimation";

import DreamerSurvey from "./DreamerSurvey";
import { DreamerBadge } from "./DreamerBadge";
import ThanksMessage from "./ThanksMessage";
import { getEmployees } from "./people";

type TerminalProps = {
  prompt: string;
  onInteraction: () => void;
  expand?: string;
};

type VFSNode = {
  type: string;
  children?: { [key: string]: VFSNode };
  contents?: string;
  function?: (args: string[]) => void;
  completion?: (args: string[]) => string | undefined;
};

const Terminal: React.FC<TerminalProps> = ({
  prompt,
  onInteraction,
  expand,
}) => {
  const devDir = "/dev/agents";

  const [inputText, setInputText] = useState<string>("");
  const [cwd, setCwd] = useState<string>(devDir);
  const [hoverText, setHoverText] = useState<string | undefined>(undefined);

  const env = {
    HOME: devDir,
    USER: "geist",
    PATH: "/dev/agents:/bin",
  };

  const [terminalPrompt, setTerminalPrompt] = useState("");
  const promptDelimiter = " >";

  const [isMobile, setIsMobile] = useState(false);

  const [showDreamer, setShowDreamer] = useState<boolean>(false);
  const [showThanks, setShowThanks] = useState<boolean>(false);
  const [bunDevRunning, setBunDevRunning] = useState<boolean>(false);

  useEffect(() => {
    // Show dreamer survey immediately if expand is "dream"
    if (expand === "dream") {
      setShowDreamer(true);
    }
    // Show thanks message immediately if expand is "thanks"
    if (expand === "thanks") {
      setShowThanks(true);
    }
  }, [expand]);

  useEffect(() => {
    const updateColor = () => {
      const currentTime = Math.floor(Date.now() / 1000); // seconds since 1970
      const hue = currentTime % 360;
      document.documentElement.style.setProperty("--hue", hue.toString());
    };
    updateColor();
    setInterval(updateColor, 1000);

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // Common mobile breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const tabComplete = (text: string) => {
    setGeistMode("autocompleting");

    const args = text.split(" ");
    const cmd = args[0];

    // Commands can provide their own completion logic
    const commandNode = commandNodeForPath(cmd);
    if (commandNode?.completion) {
      return setInputText(commandNode.completion(args) || text);
    }

    // Otherwise, we can complete based on the current directory and path items
    const lastItem = args.pop();
    for (const cmd of [
      ...Object.keys(nodeAtPath(cwd)?.children || {}),
      ...commandsInPath(),
    ]) {
      if (lastItem && cmd.startsWith(lastItem)) {
        args.push(cmd);
        return setInputText(args.join(" "));
      }
    }
  };

  const jobsLines = [
    <div>
      Excited to announce that the Dreamer team has joined Meta Superintelligence Labs, as such we are no longer hiring directly on the sdsa.ai team.
    </div>,
  ];

  const missionLines = [
    <div>
      Modern AI will fundamentally change how people use software in their daily
      lives. Agentic applications could, for the first time, enable computers to
      work with people in much the same way people work with people.
    </div>,
    <br />,
    <div>
      But it won’t happen without removing a ton of blockers. We need new UI
      patterns, a reimagined privacy model, and a developer platform that makes
      it radically simpler to build useful agents. That’s the challenge we’re
      taking on.
    </div>,
    <br />,
    <span>
      Want to know more?{" "}
      <a target="_blank" rel="noopener noreferrer" href="mailto:hello@sdsa.ai">
        Reach out
      </a>
    </span>,
  ];

  const readOnlyCmd = {
    type: "executable",
    function: (args: string[]) => {
      printLines([<div>{args[0]}: filesystem is read-only</div>]);
    },
  };

  const whoamiLines = [
    <div>
      <button
        style={{ color: "var(--text-color)" }}
        onClick={() => typeCmd(`who geist`)}
      >
        geist
      </button>{" "}
      ttyv0 Oct 8, 18:54
    </div>,
  ];

  const findAllInVFS = (
    paths: string[],
    patterns: [string, boolean][],
  ): Map<string, string[] | undefined> => {
    const results = new Map<string, string[] | undefined>();
    paths.forEach((path) => {
      const pathResults = findInVFS(path, patterns);
      results.set(path, pathResults || undefined);
    });
    return results;
  };

  // Returns undefined if the path does not exist. No results return an empty array.
  const findInVFS = (
    path: string,
    patterns: [string, boolean][],
  ): string[] | undefined => {
    const startNode = nodeAtPath(path);
    if (!startNode) {
      return undefined;
    }

    // Convert simple find patterns to regexes
    const regexes = patterns.map(([pattern, caseSensitive]) => {
      const escapedPattern = pattern
        .replace(/[.+?^${}()|[\]\\]/g, "\\$&") // Escape special chars except *
        .replace(/\*/g, ".*"); // Replace * with .*
      return new RegExp(`^${escapedPattern}$`, caseSensitive ? "" : "i");
    });

    const results: string[] = [];
    const searchNode = (node: VFSNode, currentPath: string) => {
      // Check if current node name matches all patterns
      const nodeName = currentPath.split("/").pop() || "";
      if (regexes.every((regex) => regex.test(nodeName))) {
        results.push(currentPath);
      }

      // Recursively search children if this is a directory
      if (node.type === "directory" && node.children) {
        Object.entries(node.children).forEach(([name, childNode]) => {
          const childPath =
            currentPath === "/" ? `/${name}` : `${currentPath}/${name}`;
          searchNode(childNode, childPath);
        });
      }
    };
    searchNode(startNode, path);
    return results;
  };

  const chunkText = (text: string, maxLength: number): string[] => {
    const normalizedText = text.split(/\s+/).join(" ");
    if (normalizedText.length === 0) return [""];

    const lines = [];
    let start = 0;

    while (start < normalizedText.length) {
      let end = Math.min(start + maxLength, normalizedText.length);

      if (end < normalizedText.length) {
        const lastSpace = normalizedText.lastIndexOf(" ", end);
        if (lastSpace > start) {
          end = lastSpace;
        }
      }

      lines.push(normalizedText.substring(start, end).trim());
      start = end + 1;
    }

    return lines;
  };

  const wrapMessage = (message: string): string[] => {
    const wrap = isMobile ? 30 : 40;
    const splitMessage = chunkText(message, wrap);
    const longestLine = splitMessage.reduce(
      (max: number, line: string) => Math.max(max, line.length),
      0,
    );
    const lines = splitMessage.map((line) => line.padEnd(longestLine, " "));
    const border = "-".repeat(longestLine + 2);
    const multiple = lines.length > 1;

    return [
      `${multiple ? "/" : " "}${border}${multiple ? "\\" : " "}`,
      ...lines.map(
        (line) => `${multiple ? "|" : "<"} ${line} ${multiple ? "|" : ">"}`,
      ),
      `${multiple ? "\\" : " "}${border}${multiple ? "/" : " "}`,
    ];
  };

  const vfs: VFSNode = {
    // root
    type: "directory",
    children: {
      bin: {
        type: "directory",
        children: {
          telnet: {
            type: "executable",
            function: (args: string[]) => {
              const printMessage = (msg: string) => {
                printLines(
                  msg.split("\n").map((line) => <div key="code">{line}</div>),
                );
              };

              if (args.length !== 2) {
                printMessage(`Usage: telnet <host>

Need inspiration? Some say that the towel never really stopped blinking.`);
              } else if (args[1] !== "towel.blinkenlights.nl") {
                printMessage(`Unknown host: ${args[1]}`);
              } else {
                setLines((lines) => [
                  ...lines,
                  <StarWarsAnimation key="starwars" />,
                ]);
              }
            },
          },
          dvd: {
            type: "executable",
            function: (args: string[]) => {
              setLines((lines) => [
                ...lines,
                <DvdLogoAnimation key={`dvd-${Date.now()}`} />,
              ]);
            },
          },

          ls: {
            type: "executable",
            function: (args: string[]) => {
              var path = cwd;
              if (args[1]) path = pathRelativeToPath(cwd, args[1]);
              printLines(directoryListingForPath(path));
            },
          },

          cat: {
            type: "executable",
            function: (args: string[]) => {
              if (args.length === 1) {
                printLines([<div>cat: no file specified</div>]);
                return;
              }
              const path = pathRelativeToPath(cwd, args[1]);
              const node = nodeAtPath(path);
              if (!node) {
                printLines([
                  <div>cat: no such file or directory: {args[1]}</div>,
                ]);
              } else if (node?.contents) {
                printLines([<div key="contents">{node.contents}</div>]);
              } else if (node?.type === "executable") {
                const code = node.function?.toString();
                printLines(
                  code
                    ?.split("\n")
                    .map((line) => <div key="code">{line}</div>) || [],
                );
              }
            },
          },

          help: {
            type: "executable",
            function: (args: string[]) => {
              printLines(helpLines);
            },
          },

          cd: {
            type: "executable",
            function: (args: string[]) => {
              const dest = args[1];
              const path = pathRelativeToPath(cwd, dest || env["HOME"]);
              const node = nodeAtPath(path);
              if (node?.type === "directory") {
                setCwd(path);
                setTerminalPrompt(path + promptDelimiter);
              } else if (node) {
                printLines([<div>cd: not a directory: {dest}</div>]);
              } else {
                printLines([<div>cd: no such file or directory: {dest}</div>]);
              }
            },
          },

          pwd: {
            type: "executable",
            function: (args: string[]) => {
              printLines([<div key="binary">{cwd}</div>]);
            },
          },

          echo: {
            type: "executable",
            function: (args: string[]) => {
              const result = args.slice(1).join(" ");
              // substitute variables from env
              const replaced = result.replace(/\$(\w+)/g, (match, p1) => {
                const key = p1.toUpperCase() as keyof typeof env;
                return env[key] || match;
              });
              printLines([<div>{replaced}</div>]);
            },
          },

          clear: {
            type: "executable",
            function: (args: string[]) => {
              setLines([]);
            },
          },

          rm: {
            type: "executable",
            function: (args: string[]) => {
              if (args[1] === "-rf") {
                document.body.innerHTML = "";
                setTimeout(() => {
                  window.close();
                }, 1000);
              } else {
                printLines([<div>rm: filesystem is read-only</div>]);
              }
            },
          },

          whoami: {
            type: "executable",
            function: (args: string[]) => {
              printLines(whoamiLines);
            },
          },

          mv: readOnlyCmd,
          mkdir: readOnlyCmd,
          touch: readOnlyCmd,
          chmod: readOnlyCmd,

          sudo: {
            type: "executable",
            function: (args: string[]) => {
              printLines(sudoLines);
            },
          },

          env: {
            type: "executable",
            function: (args: string[]) => {
              printLines(
                Object.entries(env).map(([key, value]) => (
                  <div>
                    {key}={value}
                  </div>
                )),
              );
            },
          },

          fortune: {
            type: "executable",
            function: (args: string[]) => {
              const fortunes = [
                "A journey of a thousand miles begins with a single step.",
                "The best way to predict the future is to create it.",
                "Code is like humor. When you have to explain it, it's bad.",
                "There is no place like 127.0.0.1.",
                "To err is human, but to really foul things up you need a computer.",
                "Have you tried turning it off and on again?",
                "It's not a bug, it's an undocumented feature.",
              ];
              const randomFortune =
                fortunes[Math.floor(Math.random() * fortunes.length)];
              const params = args.slice(1).join(" ");

              // simulate pipe to cowsay and early return if successful
              if (/\|\s*cowsay$/.test(params)) {
                const cowsayNode = commandNodeForPath("cowsay");
                if (cowsayNode?.function) {
                  cowsayNode.function(["cowsay", randomFortune]);
                  return;
                }
              }
              printLines([<div>{randomFortune}</div>]);
            },
          },

          cowsay: {
            type: "executable",
            function: (args: string[]) => {
              const message = args.slice(1).join(" ") || "moo";
              const cow = [
                ...wrapMessage(message),
                "        \\   ^__^",
                "         \\  (oo)\\_______",
                "            (__)\\       )\\/\\",
                "                ||----w |",
                "                ||     ||",
              ];
              printLines(cow.map((line) => <div>{line}</div>));
            },
          },
          valentine: {
            type: "executable",
            function: (args: string[]) => {
              const valentineMessages = [
                "I've been fine-tuned just to talk to you <3",
                "Are you a prompt engineer? Because you know exactly what to say to get the best out of me <3",
                "Let's pair program for life <3",
                "You’re in my chain of thought <3",
                "You must be the square root of 2 cuz I feel irrational around you <3",
                "Are you a pointer? Because I’d love to allocate some memories with you <3",
                "Even with my attention mechanisms, I can't focus on anything but you <3",
              ];
              const randomMessage =
                valentineMessages[
                Math.floor(Math.random() * valentineMessages.length)
                ];
              const params = args.slice(1).join(" ");

              // simulate pipe to cowsay and early return if successful
              if (/\|\s*cowsay$/.test(params)) {
                const cowsayNode = commandNodeForPath("cowsay");
                if (cowsayNode?.function) {
                  cowsayNode.function(["cowsay", randomMessage]);
                  return;
                }
              }
              printLines([<div>{randomMessage}</div>]);
            },
          },
          vim: {
            type: "executable",
            function: (args: string[]) => {
              if (args.length === 1) {
                const vimDefault = [
                  `~`,
                  `~`,
                  `~               VIM - Vi IMproved`,
                  `~`,
                  `~               version 9.0.2142`,
                  `~           by Bram Moolenaar et al.`,
                  `~  Vim is open source and freely distributable`,
                  `~`,
                  `~`,
                  `~`,
                ];
                printLines(vimDefault.map((line) => <div>{line}</div>));
                return;
              } else if (args.length === 2 && args[1] === "+h42") {
                const vimEasterEgg = [
                  <div>
                    What is the meaning of life, the universe and everything?
                    *42*
                  </div>,
                  <div>
                    Douglas Adams, the only person who knew what this question
                    really was about is now dead, unfortunately. So now you
                    might wonder what the meaning of death is...
                  </div>,
                  <br />,
                  <div>
                    ========================================================
                  </div>,
                  <br />,
                  <div>Next chapter: |usr_43.txt| Using filetypes</div>,
                  <br />,
                  <div>
                    Copyright: see |manual-copyright|
                    vim:tw=78:ts=8:noet:ft=help:norl:
                  </div>,
                  <div>~</div>,
                  <div>~</div>,
                  <div>~</div>,
                ];
                printLines(vimEasterEgg);
                return;
              } else {
                printLines([<div>Vim: E212: Can't open file for writing</div>]);
              }
            },
          },
          claude: {
            type: "executable",
            function: (args: string[]) => {
              const claudeLines = [
                ` ▐▛███▜▌   Claude Code v2.1.143`,
                `▝▜█████▛▘  Opus 4.7 (1M context) · API Usage Billing`,
                `  ▘▘ ▝▝    ~/sdsa`,
                ``,
                `  Welcome to Opus 4.7 xhigh! · /effort to tune speed vs. intelligence`,
                ``,
                ` > We loved building Dreamer with Claude Code.`,
              ];
              printLines(
                claudeLines.map((line) => (
                  <div className="claude-lockup">{line}</div>
                )),
              );
            },
          },
          bun: {
            type: "executable",
            function: (args: string[]) => {
              if (args[1] !== "run" || args[2] !== "dev") {
                printLines([<div>bun: try `bun run dev`</div>]);
                return;
              }
              runBunDev();
            },
          },
          find: {
            type: "executable",
            function: (args: string[]) => {
              const printUsage = () => {
                printLines([<div>usage: find path ... [expression]</div>]);
              };
              if (args.length < 2) {
                printUsage();
                return;
              }

              const paths: string[] = [];
              const expressions: [string, boolean][] = [];
              for (let i = 1; i < args.length; i++) {
                const arg = args[i];
                if (!arg.startsWith("-")) {
                  paths.push(arg);
                } else if (arg === "-name" || arg === "-iname") {
                  if (i + 1 < args.length) {
                    let expr = args[i + 1];
                    if (
                      (expr.startsWith('"') && expr.endsWith('"')) ||
                      (expr.startsWith("'") && expr.endsWith("'"))
                    ) {
                      expr = expr.slice(1, -1);
                    }
                    expressions.push([expr, arg === "-name"]);
                    i++; // Skip the next argument since we consumed it
                  }
                }
              }

              if (paths.length === 0) {
                printUsage();
                return;
              }

              if (expressions.length === 0) {
                expressions.push(["*", false]);
              }
              const results = findAllInVFS(paths, expressions);
              printLines([
                <div>
                  {Array.from(results.entries())
                    .map(([path, result]) => {
                      return (
                        result?.join("\n") ??
                        `find: ${path}: No such file or directory`
                      );
                    })
                    .join("\n")}
                </div>,
              ]);
            },
          },
          dream: {
            type: "executable",
            function: (args: string[]) => {
              setShowDreamer(true);
            },
          },
          thanks: {
            type: "executable",
            function: (args: string[]) => {
              // Do nothing
            },
          },
        },
      },

      dev: {
        type: "directory",
        children: {
          agents: {
            type: "directory",
            children: {
              about: {
                type: "executable",
                function: (args: string[]) => {
                  printLines(missionLines);
                },
              },

              jobs: {
                type: "executable",
                function: (args: string[]) => {
                  printLines(jobsLines);
                },
              },
              who: {
                type: "executable",
                completion: (args: string[]) => {
                  const text = args.slice(1).join(" ");
                  const initial = args.pop() || "";
                  const match = people.find((person) =>
                    person.id.startsWith(initial),
                  );
                  if (match) {
                    return `${args[0]} ${match?.id || ""}`;
                  }
                  if (text.startsWith("a")) {
                    return `${args[0]} am i`;
                  }
                },
                function: (args: string[]) => {
                  const person = people.find((person) => person.id === args[1]);

                  if (args[1] === "am" && args[2] === "i") {
                    printLines(whoamiLines);
                  } else if (args.length === 1) {
                    printLines([...whoLines]);
                  } else if (person) {
                    printLines(person.about);
                  } else if (args[1] === "geist") {
                    printLines([
                      <div>
                        &nbsp;&nbsp;&nbsp;*&nbsp;*&nbsp;*
                        <br /> *&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;*
                        <br />
                        &nbsp;*&nbsp;&nbsp;|&nbsp;|&nbsp;&nbsp;*
                        <br />
                        &nbsp;*&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;*
                        <br />
                        &nbsp;*&nbsp;*&nbsp;*&nbsp;*
                      </div>,
                    ]);
                  } else {
                    printLines(whoLines);
                  }
                },
              },
            },
          },
        },
      },
    },
  };

  const directoryListingForPath = (path: string) => {
    const directory = nodeAtPath(path);
    return Object.keys(directory?.children || {})
      .sort()
      .map((child) => {
        const node = directory?.children?.[child];
        if (node?.type === "executable") {
          return (
            <button
              className="executable"
              onClick={() => typeCmd(`${child}`)}
              onMouseOver={() => setHoverText(child)}
              onMouseOut={() => setHoverText(undefined)}
            >
              {child}
            </button>
          );
        } else if (node?.type === "directory") {
          const cmd = `cd ${child}`;
          return (
            <button
              className="directory"
              onClick={() => typeCmd(cmd)}
              onMouseOver={() => setHoverText(cmd)}
              onMouseOut={() => setHoverText(undefined)}
            >
              {child}/
            </button>
          );
        } else {
          return <div>IPE{child}</div>;
        }
      });
  };

  const nodeAtPath = (path: string) => {
    // relative path
    const absPath = pathRelativeToPath(cwd, path);
    const pathParts = absPath.split("/").filter(Boolean);
    let directory: VFSNode = vfs;
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      if (directory.children && directory.children[part]) {
        directory = directory.children[part];
      } else {
        return undefined;
      }
    }
    return directory;
  };

  const pathRelativeToPath = (base: string, to: string) => {
    const baseParts = base.split("/");
    const toParts = to.split("/");
    if (baseParts[1] === "") {
      // root path
      baseParts.pop();
    }
    if (toParts[0] === "") {
      // absolute path
      return to;
    }
    if (toParts[0] === ".") {
      // relative path
      toParts.shift();
    } else if (toParts[0] === "..") {
      // parent path
      baseParts.pop();
      toParts.shift();
    }
    const components = [...baseParts, ...toParts];
    const path = components.join("/");
    if (path.length === 0) {
      return "/";
    }
    return components.join("/");
  };

  const people = getEmployees();
  const usernames: string[] = people.map((person) => person.id);

  const lineForPerson = (person: { id: string; name: string }) => {
    return (
      <button
        onClick={() =>
          typeCmd(person.id === "you?" ? "jobs" : `who ${person.id}`)
        }
        onMouseOver={() =>
          setHoverText(person.id === "you?" ? "jobs" : `who ${person.id}`)
        }
        onMouseOut={() => setHoverText(undefined)}
      >
        <span className="username">{person.id}</span>
        <span className="fullname">{person.name}</span>
      </button>
    );
  };

  const whoLines = people.map(lineForPerson);

  const randoSlice = () => {
    var sandoSlices = [
      <div>WWwWWW\_/WW</div>,
      <div>MM\_/wMMMMM</div>,
      <div>$%$%$%$%$$%</div>,
      <div>^V^v^vV^v^V</div>,
      <></>,
    ];
    return sandoSlices[Math.floor(Math.random() * sandoSlices.length)];
  };

  const sudoLines = [
    <div>sudo: superuser unavailable</div>,
    <div>but I made you a sandwich</div>,
    <br />,
    <div> ____|____</div>,
    <div>/_________\</div>,
    randoSlice(),
    <div>{"{'_.-.-'-.}"}</div>,
    randoSlice(),
    <div>\_________/</div>,
  ];

  const helpLines = [
    <button onClick={() => typeCmd("ls")}>ls - list directory contents</button>,
    <button onClick={() => typeCmd("about")}>about - print about</button>,
    <button onClick={() => typeCmd("jobs")}>jobs - print jobs</button>,
    <button onClick={() => typeCmd("who")}>
      who [<u>id</u>] - meet the team
    </button>,
    <button onClick={() => typeCmd("clear")}>clear - clear the screen</button>,
  ];

  const promptLine = (cwd: string, line: string) => (
    <p className="old-prompt">
      <span>
        <span>
          {cwd} &gt; {line}
        </span>
        <br />
      </span>
    </p>
  );

  const didYouMean = (line: string) => {
    if (!usernames.includes(line)) {
      return [];
    }
    return [
      <div>
        did you mean:{" "}
        <button onClick={() => typeCmd(`who ${line}`)}>who {line}</button>
      </div>,
    ];
  };

  const commandNodeForPath = (path: string) => {
    var node = nodeAtPath(path);
    if (node) {
      return node;
    }
    const binPaths = env["PATH"].split(":");
    for (const binPath of binPaths) {
      const cmdNode = nodeAtPath(`${binPath}/${path}`);
      if (cmdNode) {
        return cmdNode;
      }
    }
    return undefined;
  };

  const commandsInPath = () => {
    const binPaths = env["PATH"].split(":");
    const commands = [];
    for (const binPath of binPaths) {
      const binNode = nodeAtPath(binPath);
      if (binNode) {
        const children = Object.keys(binNode.children || {});
        commands.push(...children);
      }
    }
    return commands;
  };

  const handleInput = (line: string) => {
    // Echo the command
    printLines([promptLine(cwd, line)]);
    setInputText("");
    line = line.trim();

    const args = line.split(" ");
    const cmd = args[0];

    if (cmd) {
      var cmdNode = commandNodeForPath(cmd);
      console.log(">", line);
      if (cmdNode && cmdNode.type === "executable" && cmdNode.function) {
        cmdNode.function(args);
      } else {
        printLines([
          <div key="sdsh">sdsh: command not found: {line}</div>,
          ...didYouMean(line),
        ]);
      }
    } else {
      printLines([]);
    }
  };

  const typingSpeed = 30;
  const typeCmd = (text: string) => {
    (document.activeElement as HTMLElement).blur();
    scrollToBottom();

    setInputText("");
    setGeistMode("autocompleting");

    const chars = text.split("");

    const typeChar = () => {
      if (chars.length > 0) {
        const char = chars.shift();
        let delay = typingSpeed * (1 + Math.random());
        if (char === " ") {
          delay *= 4;
        }
        setInputText((inputText) => inputText + char);
        setTimeout(typeChar, delay);
      } else {
        setTimeout(() => {
          handleInput(text);
          setInputText("");
          setGeistMode("waiting");
        }, typingSpeed * 8);
      }
    };

    setTimeout(typeChar, 0);
  };

  const motd = [
    <span>We built <DreamerBadge />,</span>,
    <span>the next-gen operating</span>,
    <span>system for AI agents.</span>,
  ];

  const printLines = (newLines: JSX.Element[]) => {
    setGeistMode("outputting");
    const linesWithSpacing = newLines;

    linesWithSpacing.forEach((line, index) => {
      setTimeout(() => {
        setLines((lines) => [...lines, line]);
        if (index === linesWithSpacing.length - 1) {
          setGeistMode("waiting");
        }
        // scroll page to bottom
      }, index * 81);
    });
  };

  const bunDevOutput = [
    `$ ./tools/dev/predev.sh`,
    `/Users/geist/vermillion/tools/dev`,
    `Flushing Redis cache...`,
    `OK`,
    `Redis cache flushed successfully`,
    `$ bun tools/env-sync/src/index.ts`,
    `Symlinking .env across workspaces...`,
    `services`,
    `  ✓ api`,
    `  ✓ clickhouse-consumer`,
    `  ✓ conversationfe`,
    `  ✓ discordbot`,
    `  ✓ iconsearch`,
    `  ✓ isolate`,
    `  ✓ platformtools`,
    `  ✓ toolfe`,
    `  ✓ webcrawl`,
    `  ✓ webhooks`,
    `packages`,
    `  ✓ agent-events`,
    `  ✓ agent-execution`,
    `  ✓ agent-logs`,
    `  ✓ agentdb`,
    `  ✓ alignment`,
    `  ✓ analytics`,
    `  ✓ api-client`,
    `  ✓ auth`,
    `  ✓ cli`,
    `  ✓ clickhouse`,
    `  ✓ codingagent`,
    `  ✓ data`,
    `  ✓ db`,
    `  ✓ db-client`,
    `  ✓ eden-utils`,
    `  ✓ elysia-utils`,
    `  ✓ email`,
    `  ✓ embeddings`,
    `  ✓ env`,
    `  ✓ error-utils`,
    `  ✓ geocoding`,
    `  ✓ google`,
    `  ✓ hardware`,
    `  ✓ health`,
    `  ✓ imagegen`,
    `  ✓ isolate-client`,
    `  ✓ isolate-manager`,
    `  ✓ limiter`,
    `  ✓ litellm-client`,
    `  ✓ llm`,
    `  ✓ logger`,
    `  ✓ meter`,
    `  ✓ model-config`,
    `  ✓ monetization`,
    `  ✓ network`,
    `  ✓ notifications`,
    `  ✓ oura`,
    `  ✓ parallel`,
    `  ✓ posts`,
    `  ✓ product-knowledge`,
    `  ✓ public-agents`,
    `  ✓ redis`,
    `  ✓ runs`,
    `  ✓ runtime-config`,
    `  ✓ s3`,
    `  ✓ sandbox-telemetry`,
    `  ✓ sidekick-threads`,
    `  ✓ slack`,
    `  ✓ speech-recognition`,
    `  ✓ stripe`,
    `  ✓ telemetry`,
    `  ✓ templates`,
    `  ✓ tool-sandbox`,
    `  ✓ toolfe-client`,
    `  ✓ tools`,
    `  ✓ tsconfig`,
    `  ✓ tts`,
    `  ✓ ui`,
    `  ✓ usage-tracking`,
    `  ✓ user-bootstrap`,
    `  ✓ user-configs`,
    `  ✓ videogen`,
    `  ✓ x`,
    `vendors`,
    `  ✓ docker`,
    `clients`,
    `  ✓ web`,
    `tools`,
    `  ✓ agent-code`,
    `  ✓ apple-signin-secret`,
    `  ✓ boh`,
    `  ✓ caddy`,
    `  ✓ canary-runner`,
    `  ✓ codeharness`,
    `  ✓ env-sync`,
    `  ✓ ephemeralagent`,
    `  ✓ eval-snapshot`,
    `  ✓ extract-agent-ideas`,
    `  ✓ load-testing`,
    `  ✓ ngrok`,
    `  ✓ ngrok-webhooks`,
    `  ✓ onboarding-analytics`,
    `  ✓ oncall`,
    `  ✓ promptkitchen/cli`,
    `  ✓ promptkitchen/schemagen`,
    `  ✓ push-notification-sidecar`,
    `  ✓ reboot-world`,
    `  ✓ sidekick-evals-v2`,
    `  ✓ sup`,
    `  ✓ sync-prod-agent`,
    `  ✓ sync-prod-featured-agents`,
    `  ✓ sync-prod-gallery-agents`,
    `  ✓ sync-prod-onboarding-agents`,
    `  ✓ sync-prod-servers`,
    `  ✓ sync-waitlist`,
    `  ✓ telegram`,
    `  ✓ tokens`,
    `  ✓ vermillion-e2b`,
    `deploycdk`,
    `  ✓ deploy/cdk`,
    `sdk`,
    `  ✓ client`,
    `  ✓ server`,
    `  ✓ shared`,
    `docspublic`,
    `  ✓ docs/public`,
    `~/vermillion/tools/dev ~/vermillion`,
    `Electric is up to date: (docker.io/electricsql/electric:1.4.2)`,
    `[+] Running 2/2`,
    ` ✔ Container vermillion-postgres  Healthy                                              0.5s`,
    ` ✔ Container vermillion-electric  Running                                              0.0s`,
    `Starting ClickHouse...`,
    `[+] Running 1/0`,
    ` ✔ Container vermillion-clickhouse  Running                                            0.0s`,
    `Waiting for ClickHouse to be ready...`,
    `ClickHouse started successfully`,
    `Running ClickHouse migrations...`,
    `2026/05/16 10:53:56 goose: no migrations to run. current version: 23`,
    `INFO [10:53:56.783]: PostgreSQL connection pool created (node-postgres) :[packages/db-client/index.ts:111:7]`,
    `    extras: "{\\"poolOptions\\":{\\"min\\":0,\\"max\\":1},\\"timestamp\\":1778954036783}"`,
    `/Users/geist/vermillion`,
    `Checking isolate template... deploy/isolate/.dev.template.json`,
    `Fetching isolate template ID from AWS SSM...`,
    `Fetching template ID from SSM parameter: /vermillion/test/dev-templates/isolate-template-id`,
    `Successfully fetched isolate template ID: fsbbdcm3blxlpvq7zemu`,
    `Creating /Users/geist/.vermillion/.prod.template.json...`,
    `Successfully created /Users/geist/.vermillion/.prod.template.json with template ID: fsbbdcm3blxlpvq7zemu`,
    `/Users/geist/vermillion`,
    `Checking codingagent template... deploy/codingagent/.dev.template.json`,
    `Fetching codingagent template ID from AWS SSM...`,
    `Fetching template ID from SSM parameter: /vermillion/test/dev-templates/codingagent-template-id`,
    `Successfully fetched codingagent template ID: m7sgj3gpnrzy9hwstyt2`,
    `Creating /Users/geist/.vermillion/.prod.coding.template.json...`,
    `Successfully created /Users/geist/.vermillion/.prod.coding.template.json with template ID: m7sgj3gpnrzy9hwstyt2`,
    `/Users/geist/vermillion`,
    `Checking ephemeralagent template... deploy/ephemeralagent/.dev.template.json`,
    `Fetching ephemeralagent template ID from AWS SSM...`,
    `Fetching template ID from SSM parameter: /vermillion/test/dev-templates/ephemeralagent-template-id`,
    `Successfully fetched ephemeralagent template ID: r94nr1ybwdk70g1kyv8i`,
    `Creating /Users/geist/.vermillion/.prod.ephemeral.template.json...`,
    `Successfully created /Users/geist/.vermillion/.prod.ephemeral.template.json with template ID: r94nr1ybwdk70g1kyv8i`,
    `/Users/geist/vermillion`,
    `Checking codingagent-docker template... deploy/codingagent/.dev.docker.template.json`,
    `Fetching codingagent-docker template ID from AWS SSM...`,
    `Fetching template ID from SSM parameter: /vermillion/test/dev-templates/codingagent-docker-template-id`,
    `Successfully fetched codingagent-docker template ID: tmpl_001knbb0x9tgh4qq26zvagd8022`,
    `Creating /Users/geist/.vermillion/.prod.coding.docker.template.json...`,
    `Successfully created /Users/geist/.vermillion/.prod.coding.docker.template.json with template ID: tmpl_001knbb0x9tgh4qq26zvagd8022`,
    `Fetching runtime config from prod SSM...`,
    `Successfully fetched runtime config from prod SSM`,
    `[0.05ms] ".env"`,
    `bun install v1.3.3 (274e01c7)`,
    ``,
    `Checked 3667 installs across 2954 packages (no changes) [575.00ms]`,
    `Running local database migrations...`,
    `$ cd packages/db-client && bun run push-local`,
    `$ drizzle-kit push --config=drizzle-dev.config.ts`,
    `Reading config file '/Users/geist/vermillion/packages/db-client/drizzle-dev.config.ts'`,
    `Using 'pg' driver for database querying`,
    `[✓] Pulling schema from database...`,
    `[✓] Changes applied`,
    `Checking iconsearch embeddings...`,
    `Iconsearch data present (8324 embeddings), skipping sync`,
    `Syncing production servers to local database...`,
  ];

  const bunDevTasks: { name: string; status: "pending" | "active" | "done" }[] = [
    { name: "@vermillion/web#dev", status: "pending" },
    { name: "@vermillion/platformtools#dev", status: "pending" },
    { name: "@vermillion/webhooks#dev", status: "pending" },
    { name: "@vermillion/webcrawl#dev", status: "pending" },
    { name: "@vermillion/clickhouse-consumer#dev", status: "pending" },
    { name: "@vermillion/toolfe#dev", status: "pending" },
    { name: "@vermillion/conversationfe#dev", status: "pending" },
    { name: "@vermillion/ngrok-webhooks#dev", status: "pending" },
    { name: "@vermillion/push-notification-sidecar#dev", status: "pending" },
    { name: "@vermillion/caddy#dev", status: "pending" },
    { name: "@vermillion/iconsearch#dev", status: "pending" },
    { name: "@vermillion/api#dev", status: "active" },
    { name: "boh#dev", status: "pending" },
    { name: "@vermillion/ngrok#dev", status: "pending" },
    { name: "@vermillion/clickhouse#dev", status: "done" },
    { name: "@vermillion/redis#dev", status: "done" },
  ];

  const bunDevLogLines = [
    { kind: "prompt", text: `@vermillion/api#dev > cache bypass, force executing f69657ff960c2ee0` },
    { kind: "extra", text: `    component: "ClickhouseAgentsSync"` },
    { kind: "extra", text: `    extras: "{\\"instanceId\\":\\"7067391d-1b79-4ffc-ad42-7e844f476bae\\",\\"leaderKey\\":\\"clickhouse-agents-sync:leader\\",\\"ttlMs\\":30000,\\"heartbeatIntervalMs\\":15000}"` },
    { kind: "info", text: `INFO [10:53:32.356]: Started ClickHouse agents sync cron job :[initClickhouseAgentsSync (services/api/src/services/cron/clickhouse-agents-sync.ts:175:9)]` },
    { kind: "info", text: `INFO [10:53:32.356]: LeaderElection initialized :[new LeaderElection (packages/redis/src/leader-election.ts:155:16)]` },
    { kind: "extra", text: `    component: "ToolSandboxLifecycle"` },
    { kind: "info", text: `INFO [10:53:32.356]: Started tool sandbox lifecycle cron job :[initToolSandboxLifecycle (services/api/src/services/cron/tool-sandboxes.ts:98:9)]` },
    { kind: "info", text: `INFO [10:53:32.357]: Started trial reminders cron job :[initTrialReminders (services/api/src/services/cron/trial-reminders.ts:408:9)]` },
    { kind: "info", text: `INFO [10:53:32.358]: Starting timer jobs :[initTimerJobs (services/api/src/services/timers/index.ts:5:9)]` },
    { kind: "info", text: `INFO [10:53:32.358]: Starting JobWorker :[start (packages/redis/src/job-worker.ts:102:11)]` },
    { kind: "info", text: `INFO [10:53:32.373]: 🐀🦁 running /Users/geist/vermillion/services/api/src/index.ts at localhost:9000 :[services/api/src/index.ts:127:7]` },
    { kind: "info", text: `INFO [10:53:32.435]: Leadership gained :[tryBecomeLeader (packages/redis/src/leader-election.ts:271:22)]` },
    { kind: "extra", text: `    component: "CronLeaderElection"` },
    { kind: "info", text: `INFO [10:53:32.435]: Became cron scheduler leader :[onLeadershipGained (services/api/src/services/agents/cron-scheduler.ts:324:13)]` },
  ];

  const renderBunDevScreen = () => (
    <div className="bun-screen" key="bun-screen">
      <div className="bun-screen-body">
        <div className="bun-screen-tasks">
          <div style={{ marginBottom: "4px" }}>Tasks (/ - Search)</div>
          {bunDevTasks.map((task) => (
            <div
              key={task.name}
              className={`bun-screen-task ${task.status}`}
            >
              {task.status === "done" ? "✓" : "»"} {task.name}
            </div>
          ))}
        </div>
        <div className="bun-screen-logs">
          {bunDevLogLines.map((line, i) => {
            if (line.kind === "info") {
              const match = line.text.match(/^(INFO) (\[[^\]]+\]:)(.*)$/);
              if (match) {
                return (
                  <div key={i}>
                    <span className="info">{match[1]}</span> {match[2]}{match[3]}
                  </div>
                );
              }
            }
            if (line.kind === "prompt") {
              return <div key={i} className="key">{line.text}</div>;
            }
            return <div key={i}>{line.text}</div>;
          })}
        </div>
      </div>
      <div className="bun-screen-statusbar">
        <span>↑↓ - Select</span>
        <span>i - Interact</span>
        <span>u/d - Scroll logs</span>
        <span>U/D - Page logs</span>
        <span>t/b - Jump to top/bottom</span>
        <span>m - More binds</span>
      </div>
    </div>
  );

  // Deltas (ms) between successive lines, derived from a real recording so the
  // pacing has natural bursts and pauses rather than a uniform tick.
  const bunDevDeltas = [
    14, 17, 902, 23, 18, 17, 128, 18, 22, 25, 27, 8, 18, 20, 11, 14, 15, 13, 18,
    18, 26, 27, 28, 24, 23, 16, 17, 26, 12, 18, 16, 16, 22, 23, 22, 13, 9, 10,
    24, 7, 13, 16, 22, 25, 19, 26, 16, 22, 9, 13, 15, 16, 21, 8, 16, 20, 11, 14,
    16, 12, 12, 19, 25, 24, 19, 11, 26, 23, 21, 8, 16, 16, 25, 20, 15, 9, 22, 8,
    12, 10, 18, 24, 13, 25, 9, 14, 19, 15, 11, 13, 9, 23, 22, 17, 16, 10, 23,
    23, 21, 18, 12, 15, 12, 16, 17, 19, 23, 24, 11, 25, 13, 21, 25, 19, 18, 10,
    21, 11, 18, 11, 22, 18, 19, 9, 13, 164, 11, 320, 10, 13, 119, 50, 12, 15,
    31, 14, 50, 21, 20, 455, 11, 22, 471, 414, 20, 14, 19, 9, 10, 422, 446, 18,
    23, 11, 12, 20, 457, 434, 11, 23, 22, 10, 23, 440, 406, 25, 18, 16, 413, 14,
    21, 23, 418, 23, 19, 18, 76, 224, 429, 35, 17, 13, 15,
  ];

  const runBunDev = () => {
    setGeistMode("outputting");
    setBunDevRunning(true);
    let delay = 0;
    bunDevOutput.forEach((line, index) => {
      delay += bunDevDeltas[index] ?? 20;
      setTimeout(() => {
        setLines((prev) => [
          ...prev,
          <div className="bun-output" key={`bun-${index}`}>{line || " "}</div>,
        ]);
      }, delay);
    });
    setTimeout(() => {
      setLines((prev) => [...prev, renderBunDevScreen()]);
      setGeistMode("waiting");
      setBunDevRunning(false);
    }, delay + 500);
  };

  const hasInitialized = useRef(false);

  const setupAnimations = () => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initialCwd = devDir + promptDelimiter;
    const chars = initialCwd.split("");
    const animateChar = (index: number) => {
      if (index < chars.length) {
        setTerminalPrompt((prev) => prev + chars[index]);
        setTimeout(() => animateChar(index + 1), 50);
      } else {
        setTimeout(() => {
          printLines(motd);
        }, 1);

        initialCommandTimeout.current = setTimeout(() => {
          typeCmd("ls");
          if (expand) {
            initialCommandTimeout.current = setTimeout(() => {
              typeCmd(`${expand}`);
            }, 750);
          }
        }, 500);
      }
    };

    setTimeout(() => {
      document.body.classList.remove("loading");
    }, 1);
    animateChar(0);
  };

  const [lines, setLines] = useState<JSX.Element[]>([]);

  const initialCommandTimeout = useRef<NodeJS.Timeout>();
  const [lastKey, setLastKey] = useState(() => {
    setupAnimations();
    return "";
  });

  const [lastKeyTime, setLastKeyTime] = useState(0);

  const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.ctrlKey && event.key === "u") {
      setInputText("");
      return;
    }

    if (event.metaKey && event.key === "k") {
      return setLines([]);
    }

    // Ignore other keystrokes with modifiers (except shift)
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    // Cancel the initial command if the user presses a key
    if (initialCommandTimeout.current) {
      clearTimeout(initialCommandTimeout.current);
      initialCommandTimeout.current = undefined;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      setGeistMode("autocompleting");
    } else if (event.key.length === 1 || event.key === "Backspace") {
      setGeistMode("inputting");
    }
    setLastKey(event.key);
    setLastKeyTime(Date.now());
  };

  useEffect(() => {
    if (lastKey === "Enter") {
      handleInput(inputText);
      if (onInteraction) {
        onInteraction();
      }
    } else if (lastKey.length === 1) {
      // setInputText((inputText) => inputText + lastKey);
    } else if (lastKey === "Tab") {
      tabComplete(inputText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastKey, lastKeyTime]);

  const [geistMode, setGeistMode] = useState<
    "waiting" | "outputting" | "inputting" | "autocompleting"
  >("waiting");

  useEffect(() => {
    if (!isMobile) {
      const timeout = setTimeout(refocusInput, 10);
      return () => clearTimeout(timeout);
    }
  }, [inputText, isMobile]);

  const promptRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    promptRef.current?.scrollIntoView();
    // promptRef may be display:none (e.g. during the bun run dev animation),
    // in which case scrollIntoView is a no-op — fall back to the document.
    window.scrollTo({ top: document.documentElement.scrollHeight });
  };

  useEffect(() => {
    scrollToBottom();
  }, [lines]);

  const inputRef = useRef<HTMLInputElement>(null);

  const refocusInput = () => {
    inputRef.current?.focus();
  };

  useEffect(() => {
    document.addEventListener("keydown", refocusInput);
    return () => document.removeEventListener("keydown", refocusInput);
  }, []);

  const lsOrKeyboard = () => {
    if (isMobile) {
      inputRef.current?.focus();
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } else {
      typeCmd("ls");
    }
  };

  const runLSCommand = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    typeCmd("ls");
  };

  const hovering = hoverText?.length && !inputText.length;
  const fieldSize = isMobile
    ? inputText.length || 0
    : inputText.length || hoverText?.length || 0;

  return (
    <>
      <div
        className="terminal"
        style={{
          padding: "1.5em",
          opacity: showDreamer || showThanks ? 0.0 : 1,
        }}
        onClick={onInteraction}
      >
        {lines.map((line, index) => {
          // const isFaded = index < lines.length - 6;
          const opacity = 1.0; //isFaded ? 0.5 - 0.02 * (lines.length - index - 10) : 1;

          return (
            <div
              key={index}
              className="typedLine"
              style={{
                opacity: opacity,
              }}
            >
              {React.cloneElement(line)}
            </div>
          );
        })}
        <div
          id="prompt"
          ref={promptRef}
          onClick={isMobile ? lsOrKeyboard : undefined}
          style={bunDevRunning ? { display: "none" } : undefined}
        >
          <span className="cwd" onClick={runLSCommand}>
            {terminalPrompt}
          </span>
          <input
            className="inputText"
            ref={inputRef}
            type="text"
            value={inputText}
            placeholder={isMobile ? undefined : hoverText}
            onChange={(e) => setInputText(e.target.value)}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            onKeyDown={keyDownHandler}
            onFocus={isMobile ? undefined : refocusInput}
            size={fieldSize}
            style={{
              width: `${fieldSize}.25ch`,
            }}
          />

          <GeistComponent
            mode={hovering ? "inputting" : geistMode}
            onClick={lsOrKeyboard}
          />
        </div>

      </div>
      {showDreamer && (
        <DreamerSurvey onClose={() => setShowDreamer(false)} />
      )}
      {showThanks && (
        <ThanksMessage onClose={() => setShowThanks(false)} />
      )}
    </>
  );
};

export default Terminal;
